import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, CheckCircle, HelpCircle } from 'lucide-react';

// Import components
import { AISnippetBlockFeatured } from '@/components/AISnippetBlock';
import { PeopleAlsoAsk } from '@/components/PeopleAlsoAsk';
import { SchemaGenerator, generateArticleSchema, generateFAQSchema, generateHowToSchema } from '@/components/SchemaGenerator';
import { BreadcrumbNav } from '@/components/BreadcrumbNav';
import { Footer } from '@/components/Footer';
import { MainNavigation } from '@/components/MainNavigation';
import { GridBackground } from '@/components/GridBackground';
import { CTA1 } from '@/components/CTABanners';
import { PillarContentRenderer } from '@/components/PillarContentRenderer';
import { ArticleFooter } from '@/components/ArticleFooter';
import { loadAllArticles } from '@/services/articleLoader';

// Legacy data for detailed sections (will be migrated fully into JSON later)
import { whatIsIPTV2026 } from '@/data/pillarPages/whatIsIPTV2026';

/**
 * PILLAR PAGE: What is IPTV 2026
 * 
 * Primary Keyword: "what is iptv" (Volume: 1000+, KD: 62)
 * Expected Impact: +200 organic impressions/day
 * 
 * Fully AIO-optimized with:
 * - Quick Answer block (AI extraction)
 * - 6 key points (listicle extraction)
 * - 10+ FAQ items (Google People Also Ask)
 * - Full schema markup (Article + FAQ + HowTo)
 * - Internal linking strategy
 * - Conversion CTAs
 */

const PILLAR_SLUG = 'what-is-iptv-2026';

export const WhatIsIPTV2026Page = () => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const articles = await loadAllArticles();
        const found = articles.find(a => a.slug === PILLAR_SLUG);
        if (mounted) {
          setArticle(found || null);
          setLoading(false);
        }
      } catch (e) {
        console.warn('Failed to load pillar article from /articles:', e);
        if (mounted) {
          setArticle(null);
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">
        Loading article...
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-primary inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  // Prepare schema data from JSON article (source of truth in /articles)
  const articleSchema = generateArticleSchema({
    title: article.title,
    excerpt: article.excerpt,
    url: 'https://xtreamgate.com/blog/what-is-iptv-2026',
    date: article.date,
    lastUpdated: article.lastUpdated || article.date,
    category: article.category,
    keywords: (article.seoKeywords || []).join(', ')
  });

  const faqSchema = generateFAQSchema(article.faqItems || []);

  const howToSchema = generateHowToSchema({
    name: 'How to Set Up and Start Using IPTV',
    description: 'Step-by-step guide to getting started with IPTV in 5 minutes',
    steps: whatIsIPTV2026.howToWorks
  });

  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Blog', url: '/blog' },
    { name: 'IPTV Basics', url: '/blog?category=iptv-basics' },
    { name: article.title, url: '/blog/what-is-iptv-2026' }
  ];

  return (
    <div className="min-h-screen relative">
      <GridBackground />
      <MainNavigation />

      {/* SEO & Schema Markup */}
      <Helmet>
        <title>{article.title}</title>
        <meta name="description" content={article.excerpt} />
        <meta name="keywords" content={(article.seoKeywords || []).join(', ')} />
        <link rel="canonical" href="https://xtreamgate.com/blog/what-is-iptv-2026" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:type" content="article" />
      </Helmet>

      {/* Schema Generator - All structured data */}
      <SchemaGenerator
        article={articleSchema}
        faqItems={article.faqItems || []}
        howTo={howToSchema}
        breadcrumbs={breadcrumbs}
        // Organization schema is now handled globally
        includeWebSite={false}
      />

      <div className="max-w-4xl mx-auto px-4 py-12 relative z-10">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-8"
        >
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-primary hover:gap-3 transition-all text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </motion.div>

        {/* Breadcrumb Navigation */}
        <BreadcrumbNav items={breadcrumbs} currentPageTitle={article.title} />

        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-4">
            <BookOpen className="w-8 h-8 text-primary" />
            <span className="px-3 py-1 bg-primary/20 border border-primary/30 rounded-full text-xs font-semibold text-primary">
              {article.category}
            </span>
            {article.pillarNumber && (
              <span className="px-3 py-1 bg-primary/20 border border-primary/40 rounded-full text-xs font-semibold text-primary/90">
                {article.pillarNumber}
              </span>
            )}
            <span className="px-3 py-1 bg-green-500/20 border border-green-500/30 rounded-full text-xs font-semibold text-green-400">
              ✓ Updated {article.lastUpdated || article.date}
            </span>
          </div>

          <h1 className="text-5xl font-bold mb-4 leading-tight">
            {article.title}
          </h1>

          <p className="text-xl text-muted-foreground mb-6">
            {article.excerpt}
          </p>

          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>{article.readTime}</span>
            <span>•</span>
            <span>Published {article.date}</span>
          </div>
        </motion.div>

        {/* AI Summary Block - PRIMARY EXTRACTION TARGET */}
        <AISnippetBlockFeatured
          title="Quick Answer"
          quickAnswer={article.quickAnswer}
          expandedAnswer={article.expandedAnswer}
          keyPoints={article.keyPoints}
          lastUpdated={article.lastUpdated || article.date}
          ctaText="Get Free Trial"
          ctaLink="https://xtreamcube.shop/freetrialiptv/"
        />

        {/* Main Content */}
        <motion.article
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="prose prose-invert max-w-none mb-12"
        >
          <div className="glass rounded-xl p-8 space-y-12">
            {/* Section 1: Definition */}
            <section>
              <h2 className="text-3xl font-bold mb-4">{whatIsIPTV2026.mainContent.section1.heading}</h2>
              <PillarContentRenderer content={whatIsIPTV2026.mainContent.section1.content} />
            </section>

            {/* Section 2: Comparison */}
            <section>
              <h2 className="text-3xl font-bold mb-4">{whatIsIPTV2026.mainContent.section2.heading}</h2>
              <PillarContentRenderer content={whatIsIPTV2026.mainContent.section2.content} />
            </section>

            {/* Section 3: Why IPTV is Exploding */}
            <section>
              <h2 className="text-3xl font-bold mb-4">{whatIsIPTV2026.mainContent.section3.heading}</h2>
              <PillarContentRenderer content={whatIsIPTV2026.mainContent.section3.content} />
            </section>

            {/* Section 4: Legality */}
            <section>
              <h2 className="text-3xl font-bold mb-4">{whatIsIPTV2026.mainContent.section4.heading}</h2>
              <PillarContentRenderer content={whatIsIPTV2026.mainContent.section4.content} />
            </section>

            {/* Section 5: Getting Started */}
            <section>
              <h2 className="text-3xl font-bold mb-4">{whatIsIPTV2026.mainContent.section5.heading}</h2>
              <PillarContentRenderer content={whatIsIPTV2026.mainContent.section5.content} />
            </section>

            {/* Section 6: Equipment */}
            <section>
              <h2 className="text-3xl font-bold mb-4">{whatIsIPTV2026.mainContent.section6.heading}</h2>
              <PillarContentRenderer content={whatIsIPTV2026.mainContent.section6.content} />
            </section>
          </div>
        </motion.article>

        {/* FAQ Section - Google People Also Ask */}
        <PeopleAlsoAsk
          faqItems={article.faqItems && article.faqItems.length ? article.faqItems : whatIsIPTV2026.faqItems}
          title="People Also Ask About IPTV"
          variant="default"
        />

        {/* Internal Links Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="my-12 glass rounded-xl p-8"
        >
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
            <HelpCircle className="w-6 h-6 text-primary" />
            Explore Related Topics
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {whatIsIPTV2026.internalLinks.map((link, idx) => (
              <Link
                key={idx}
                to={`/blog/${link.slug}`}
                className="p-4 bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 transition-colors group"
              >
                <p className="text-primary group-hover:text-primary/80 font-semibold mb-1">
                  {link.anchor}
                </p>
                <p className="text-sm text-muted-foreground">
                  {link.context}
                </p>
              </Link>
            ))}
          </div>
        </motion.div>

        {/* CTA Section - Premium Banner */}
        <div className="my-12">
          <CTA1
            headline={whatIsIPTV2026.cta.heading}
            subtext={whatIsIPTV2026.cta.subheading}
            buttonText={whatIsIPTV2026.cta.primaryCta.text}
            buttonUrl={whatIsIPTV2026.cta.primaryCta.url}
          />
        </div>

        {/* Article Footer - Share & Navigation */}
        <ArticleFooter
          title={whatIsIPTV2026.title}
          nextArticleTitle="Best IPTV 2026"
          nextArticleUrl="/blog/best-iptv-usa-2026"
        />
      </div>

      <Footer />
    </div>
  );
};

export default WhatIsIPTV2026Page;